import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Lands here from Google's OAuth redirect: /auth/google/callback?code=...&state=...
 * Posts the code to /api/auth/google/exchange together with the exact redirect_uri
 * we used to start the flow (window.location.origin + "/auth/google/callback").
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthGoogleCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const googleError = params.get("error");
      if (googleError) {
        setError(`Google sign-in was cancelled (${googleError}). Redirecting…`);
        setTimeout(() => navigate("/login"), 1800);
        return;
      }
      if (!code) {
        setError("No authorisation code returned. Redirecting…");
        setTimeout(() => navigate("/login"), 1500);
        return;
      }
      const redirect_uri = window.location.origin + "/auth/google/callback";
      try {
        const { data } = await api.post("/auth/google/exchange", { code, redirect_uri });
        if (data?.token) localStorage.setItem("pg_token", data.token);
        if (data?.user) setUser(data.user);
        // Resolve the intent the user picked on the landing page so they
        // land in the right "add" flow, not on an empty dashboard.
        let intent = null;
        try { intent = window.localStorage.getItem("pg_intent"); } catch { /* no-op */ }
        // Fire GA4 conversion event. We treat first-time auth as signup;
        // returning users still fire it (it's the closest signal we have
        // without a separate `is_new_user` flag from the server).
        try {
          if (typeof window !== "undefined" && window.gtag) {
            window.gtag("event", "signup_complete", { method: "google", intent: intent || "none" });
          }
        } catch { /* no-op */ }
        const intentMap = { track: "/dashboard/add", wishlist: "/dashboard/wishlist", vault: "/dashboard/vault/new" };
        const target = (intent && intentMap[intent]) || "/dashboard";
        try { if (intent) window.localStorage.removeItem("pg_intent"); } catch { /* no-op */ }
        window.history.replaceState({}, "", target);
        navigate(target, { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Google sign-in failed. Redirecting…");
        setTimeout(() => navigate("/login"), 2000);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center text-sm font-mono-pg" data-testid="auth-google-callback">
      {error || "Signing you in with Google securely…"}
    </div>
  );
}
